<script setup>
import { useRouter } from 'vue-router';
import { onMounted, ref } from 'vue';
import { useStorage } from '@/composables/use-storage';
import ChangeGroupForm from '@/pages/user/ChangeGroupForm.vue';
import { useLayoutComponents } from '@/composables/layout.js';
import { useToast } from '@/composables/use-toast';
import axios from 'axios';

const { addToast } = useToast();
const { Modal } = useLayoutComponents();


const { hasPermission, userInfo, isLoggedIn } = useStorage();

const router = useRouter();
const isVisible = ref(false);

const showChangeGroupModal = ref(false);
const changeGroupFormData = ref({});

const handleIssueRequest = () => {
  if(!isLoggedIn()) {
    router.push({ name: 'login' });
    return;
  }
  if (!hasPermission('rfp-create')) {
    showChangeGroupModal.value = true;
    return;
  }
  router.push({ name: 'rfp-create' });
};

const handleChangeGroupFormData = (data) => {
    changeGroupFormData.value = data;
};

const handleChangeGroupSubmit = async () => {
    await validateChangeGroupForm(changeGroupFormData.value);
};


const validateChangeGroupForm = async (formData) => {
    try {
        if (formData.needsRFP === 'yes' && !formData.rfpDescription.trim()) {
            addToast('Please fill in the description of the RFP', 'error');
            return;
        }

        
        if (formData.wantToSell === 'yes' && !formData.sellDescription.trim()) {
            addToast('Please fill in the description of the datasets you want to sell', 'error');
            return;
        }

        if(formData.needsRFP === 'yes' || formData.wantToSell === 'yes') {
            const data = {
                user_id: userInfo.value.id,
                form: formData
            }
            
            const response = await axios.post('api/change-group-requests', data);

            if (response.data && response.status === 200 || response.status === 201) {
                addToast('Request sent successfully', 'success');
                showChangeGroupModal.value = false;
            } else {
                addToast('Error sending request. Please try again.', 'error');
               
            }
        } else {
            addToast('Please select at least one option', 'error');
        }
    } catch (error) {
        console.error('Error sending request:', error);
        
    }
}

onMounted(() => {
  setTimeout(() => {
    isVisible.value = true;
  }, 100);
  
  setTimeout(() => {
    if (!isVisible.value) {
      isVisible.value = true;
    }
  }, 1000);
});
</script>

<template>  
  <div class="group relative rounded-xl  bg-echoes-background-black border border-echoes-border-gray hover:border-echoes-primary shadow-sm hover:shadow-echoes-primary/30 transition-all duration-200 p-4 sm:p-6 min-h-[280px] sm:min-h-[320px] animate-card" 
       :class="{ 'card-visible': isVisible }"
       :style="{ '--animation-order': 0 }">
    <div class="flex flex-col h-full items-center text-center justify-center">
      <!-- Content Section -->
      <div class="flex flex-col items-center gap-3 sm:gap-4">
     
        <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" fill="none" viewBox="0 0 96 96" id="idea" class="text-yellow-500 w-12 h-12 sm:w-14 sm:h-14">
  <path stroke="currentColor" stroke-width="5" d="M57.1961 72.5975C57.1961 66.9235 60.7892 61.993 64.339 57.5666C67.2568 53.9282 69 49.3226 69 44.3134C69 32.5424 59.3741 23 47.5 23C35.6259 23 26 32.5424 26 44.3134C26 49.3226 27.7432 53.9282 30.661 57.5666C34.2108 61.993 37.8039 66.9235 37.8039 72.5976V73C37.8039 76.3137 40.4902 79 43.8039 79H51.1961C54.5098 79 57.1961 76.3137 57.1961 73V72.5975Z"></path>
  <path stroke="currentColor" stroke-width="5" d="M43 78V81.5C43 83.9853 45.0147 86 47.5 86V86C49.9853 86 52 83.9853 52 81.5V78"></path>
  <path fill="currentColor" d="M76.7516 49C75.6704 49 74.8226 48.0811 74.8226 47V47 47C74.8226 45.9189 75.6704 45 76.7516 45H83.9301C85.0733 45 86 45.8954 86 47 86 48.1046 85.0733 49 83.9301 49H76.7516zM18.2484 45C19.3296 45 20.1774 45.9189 20.1774 47V47 47C20.1774 48.0811 19.3296 49 18.2484 49H11.0699C9.92672 49 9 48.1046 9 47 9 45.8954 9.92672 45 11.0699 45H18.2484zM75.135 33.2224C74.1886 33.8058 72.9495 33.4417 72.3936 32.4789V32.4789 32.4789C71.8274 31.4982 72.0965 30.2236 73.0605 29.6293L78.0819 26.5336C79.0741 25.9607 80.3428 26.3007 80.9156 27.2929 81.4885 28.2851 81.1485 29.5538 80.1563 30.1267L75.135 33.2224zM64.8194 20.7354C64.2583 21.7073 62.9931 21.9941 62.0212 21.433V21.433 21.433C61.0494 20.8719 60.6652 19.6328 61.2263 18.661L63.9587 13.9283C64.5316 12.9361 65.8003 12.5961 66.7925 13.1689 67.7847 13.7418 68.1247 15.0105 67.5518 16.0027L64.8194 20.7354zM50 15.4985C50 16.8547 48.8563 17.9241 47.5 17.9241V17.9241 17.9241C46.1437 17.9241 45 16.8547 45 15.4985L45 10.1574C45 8.96591 46.1193 8 47.5 8 48.8807 8 50 8.96591 50 10.1574L50 15.4985zM33.7736 18.6607C34.3347 19.6326 33.9506 20.8717 32.9787 21.4328V21.4328 21.4328C32.0068 21.9939 30.7416 21.707 30.1805 20.7352L27.4481 16.0025C26.8753 15.0103 27.2152 13.7415 28.2074 13.1687 29.1996 12.5959 30.4683 12.9358 31.0412 13.928L33.7736 18.6607zM21.9395 29.6299C22.9034 30.2242 23.1726 31.4988 22.6064 32.4795V32.4795 32.4795C22.0505 33.4423 20.8113 33.8064 19.865 33.2229L14.8435 30.1271C13.8513 29.5542 13.5113 28.2855 14.0842 27.2933 14.657 26.3011 15.9258 25.9611 16.918 26.534L21.9395 29.6299z"></path>
</svg>
        
       
        
        <div class="space-y-3">
          <h2 class="text-white text-base sm:text-lg font-semibold leading-tight">Can't find the data you need?</h2>
          <p class="text-gray-400 text-xs sm:text-sm mx-auto max-w-xs px-1">Send a Request to our network of data providers and experts to get it.</p>
        </div>

        <button
          @click="handleIssueRequest"
          class="mt-2 bg-black text-white/80 hover:text-white px-4 py-2.5 sm:px-6 sm:py-2.5 rounded-full border border-echoes-border-gray hover:border-white/20 hover:bg-white/10 transition-all duration-200 flex items-center justify-center gap-2 w-full sm:w-auto whitespace-nowrap"
        >
        <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-yellow-500" fill="currentColor" viewBox="0 0 24 24" id="shine">
  <path d="M17.2099609 12.5224609l-1.5336914-.3291016c-1.9350586-.4160156-3.4536133-1.9345703-3.8691406-3.8691406l-.3295898-1.5341797c-.0986328-.4609375-.5063477-.7900391-.9775391-.7900391s-.8789062.3291016-.9775391.7900391l-.3295898 1.5341797c-.4155273 1.9345703-1.934082 3.453125-3.8691406 3.8691406l-1.5336914.3291016c-.4609375.0986328-.7900391.5058594-.7900391.9775391s.3291016.8789062.7900391.9775391l1.5336914.3291016c1.9350586.4160156 3.4536133 1.9345703 3.8691406 3.8691406l.3295898 1.5341797c.0986328.4609375.5063477.7900391.9775391.7900391s.8789062-.3291016.9775391-.7900391l.3295898-1.5341797c.4155273-1.9345703 1.934082-3.453125 3.8691406-3.8691406l1.5336914-.3291016c.4609375-.0986328.7900391-.5058594.7900391-.9775391s-.3291016-.8789062-.7900391-.9775391zm-6.7099609 3.9443359c-.6640625-1.265625-1.7011719-2.3017578-2.9663086-2.9667969 1.2651367-.6640625 2.3022461-1.7011719 2.9663086-2.9667969.6640625 1.265625 1.7011719 2.3017578 2.9663086 2.9667969-1.2651367.6640625-2.3022461 1.7011719-2.9663086 2.9667969zM20.2949219 6.0439453l-.9155273-.2822266c-.5463867-.1679688-.9731445-.5947266-1.1416016-1.1416016l-.2822266-.9150391c-.1293945-.4189453-.5166016-.7050781-.9555664-.7050781s-.8261719.2861328-.9555664.7050781l-.2822266.9160156c-.168457.5458984-.5952148.9726562-1.1420898 1.140625l-.9150391.2822266c-.4189453.1298828-.7050781.5175781-.7050781.9560547s.2861328.8261719.7050781.9560547l.9155273.2822266c.5463867.1679688.9731445.5947266 1.1416016 1.1416016l.2822266.9150391c.1293945.4189453.5166016.7050781.9555664.7050781s.8261719-.2861328.9555664-.7050781l.2822266-.9160156c.168457-.5458984.5952148-.9726562 1.1420898-1.140625l.9150391-.2822266c.4189453-.1298828.7050781-.5175781.7050781-.9560547s-.2861328-.8261719-.7050781-.9560547zm-3.2949219 1.4882812c-.1586914-.1953125-.3369141-.3730469-.5317383-.5322266.1948242-.1591797.3730469-.3369141.5317383-.5322266.1586914.1953125.3369141.3730469.5317383.5322266-.1948242.1591797-.3730469.3369141-.5317383.5322266z"></path>
</svg>
          <span class="text-xs sm:text-sm">Issue Request</span>
        </button>
      </div>
    </div>
  </div>
  <Modal v-if="showChangeGroupModal" 
    :show="showChangeGroupModal" 
    :title="'Request access to more features?'" 
    :close-on-click-outside="false"
    @close="() => showChangeGroupModal = false"
    :button-text="'Submit'"
    :button-loading-text="'Submitting...'"
    @save="handleChangeGroupSubmit">
    <ChangeGroupForm :key="'change-group-form'" @change="handleChangeGroupFormData" />
</Modal>
</template>

<style scoped>
.animate-card {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.4s ease;
  transition-delay: calc(var(--animation-order) * 5ms);
  pointer-events: none;
}

.card-visible {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.animate-card:has(.card-visible),
.animate-card.card-visible {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.animate-card:hover {
  transform: translateY(-2px);
  transition: all 0.3s ease;
}

.card-visible:hover {
  transform: translateY(-2px);
}

@media (prefers-reduced-motion: reduce) {
  .animate-card {
    transition: none;
    opacity: 1;
    transform: none;
  }
  
  .animate-card:hover {
    transform: none;
  }
}
</style> 